import { inject } from '@angular/core';
import { Router, Routes, UrlTree } from '@angular/router';
import { authGuard } from 'src/lib/guards/auth.guard';
import { bookingDetailDocumentResolver } from './resolvers/booking-detail-document.resolver';
import { bookingDetailResolver } from './resolvers/booking-detail.resolver';
import { bookingDocumentListResolver } from './resolvers/booking-document-list.resolver';
import { brandDetailAccountResolver } from 'src/app/features/brand/base/brand-detail-account.resolver';
import { documentTitleResolver } from './resolvers/document-title.resolver';
import { scheduleBookingListResolver } from 'src/app/features/schedule';

/**
 * legacy `agent-{type}/:id` URL 을 통합 라우트 + `?view=agent` queryParam 으로
 * 변환하는 함수형 redirect 팩토리.
 *
 * Phase 4-A 도입. 외부 북마크/이메일 링크 호환을 위해 6개월 이상 유지 권장.
 */
function redirectToAgentView(targetPath: string) {
  return (input: {
    params: Record<string, string>;
    queryParams: Record<string, string>;
  }): UrlTree => {
    const router = inject(Router);
    const id = input.params['id'];
    const typeQuery = input.queryParams['type']
      ? `&type=${input.queryParams['type']}`
      : '';
    return router.parseUrl(
      `/document/${targetPath}/${id}?view=agent${typeQuery}`,
    );
  };
}

export const documentRoutes: Routes = [
  {
    path: 'document',
    canActivate: [authGuard],
    title: documentTitleResolver,
    loadComponent: () =>
      import('./components/print-layout/print-layout.component').then(
        (m) => m.PrintLayoutComponent,
      ),
    children: [
      {
        path: 'booking-estimate/:id/:estimateId',
        loadComponent: () =>
          import(
            './components/documents/booking-estimate-document/booking-estimate-document.component'
          ).then((m) => m.BookingEstimateDocumentComponent),
        resolve: {
          data: bookingDetailDocumentResolver,
        },
        data: {
          type: 'booking-estimate-fit',
        },
      },
      {
        path: 'booking-package-estimate/:id',
        loadComponent: () =>
          import(
            './components/documents/booking-package-estimate-document/booking-package-estimate-document.component'
          ).then((m) => m.BookingPackageEstimateDocumentComponent),
        resolve: {
          data: bookingDetailDocumentResolver,
          scheduleList: scheduleBookingListResolver,
        },
        data: {
          type: 'booking-estimate',
          viewType: 'admin',
        },
      },
      {
        // Legacy alias — Phase 4-A 에서 통합 라우트 + ?view=agent 로 redirect.
        // 외부 북마크/이메일 호환 위해 유지 (6 개월 이상). cleanup 시 제거.
        path: 'agent-booking-package-estimate/:id',
        redirectTo: redirectToAgentView('booking-package-estimate'),
      },
      {
        path: 'booking-package-confirmation/:id',
        loadComponent: () =>
          import(
            './components/documents/booking-package-confirmation-document/booking-package-confirmation-document.component'
          ).then((m) => m.BookingPackageConfirmationDocumentComponent),
        resolve: {
          data: bookingDetailDocumentResolver,
          scheduleList: scheduleBookingListResolver,
        },
        data: {
          type: 'booking-confirmation',
          viewType: 'admin',
        },
      },
      {
        // Legacy alias → 통합 라우트 + ?view=agent
        path: 'agent-booking-package-confirmation/:id',
        redirectTo: redirectToAgentView('booking-package-confirmation'),
      },
      {
        path: 'prepare/:id',
        loadComponent: () =>
          import(
            './components/documents/prepare-document/prepare-document.component'
          ).then((m) => m.PrepareDocumentComponent),
        resolve: {
          data: bookingDetailDocumentResolver,
          scheduleList: scheduleBookingListResolver,
          brand: brandDetailAccountResolver,
        },
        data: {
          type: 'arrangement',
        },
      },
      {
        path: 'final-arrangement/:id',
        loadComponent: () =>
          import(
            './components/documents/prepare-document/prepare-document.component'
          ).then((m) => m.PrepareDocumentComponent),
        resolve: {
          data: bookingDetailDocumentResolver,
          scheduleList: scheduleBookingListResolver,
          brand: brandDetailAccountResolver,
        },
        data: {
          type: 'final-arrangement',
        },
      },
      {
        path: 'booking-order/:id',
        loadComponent: () =>
          import(
            './components/documents/booking-order-document/booking-order-document.component'
          ).then((m) => m.BookingOrderDocumentComponent),
        resolve: {
          data: bookingDetailDocumentResolver,
          scheduleList: scheduleBookingListResolver,
          brand: brandDetailAccountResolver,
        },
        data: {
          type: 'booking-order',
        },
      },
      {
        path: 'confirmation/:id',
        loadComponent: () =>
          import(
            './components/documents/prepare-confirmation-document/prepare-confirmation-document.component'
          ).then((m) => m.PrepareConfirmationDocumentComponent),
        resolve: {
          data: bookingDetailDocumentResolver,
          scheduleList: scheduleBookingListResolver,
          brand: brandDetailAccountResolver,
        },
        data: {
          type: 'confirmation',
        },
      },
      {
        path: 'invoice/:id',
        loadComponent: () =>
          import(
            './components/documents/invoice-document/invoice-document.component'
          ).then((m) => m.InvoiceDocumentComponent),
        resolve: {
          data: bookingDetailDocumentResolver,
          brand: brandDetailAccountResolver,
        },
        data: {
          type: 'invoice',
          viewType: 'admin',
        },
      },
      {
        // Legacy alias → 통합 라우트 + ?view=agent
        path: 'agent-invoice/:id',
        redirectTo: redirectToAgentView('invoice'),
      },
      {
        path: 'schedule/:id',
        loadComponent: () =>
          import(
            './components/documents/schedule-document/schedule-document.component'
          ).then((m) => m.ScheduleDocumentComponent),
        resolve: {
          data: bookingDetailResolver,
          scheduleList: scheduleBookingListResolver,
        },
        data: {
          type: 'schedule',
          viewType: 'admin',
        },
      },
      {
        // Legacy alias → 통합 라우트 + ?view=agent
        path: 'agent-schedule/:id',
        redirectTo: redirectToAgentView('schedule'),
      },
      {
        path: 'hotel-confirmation/:id',
        loadComponent: () =>
          import(
            './components/documents/hotel-confirmation-document/hotel-confirmation-document.component'
          ).then((m) => m.HotelConfirmationDocumentComponent),
        resolve: {
          data: bookingDetailResolver,
        },
        data: {
          type: 'hotel-confirmation',
          mrhstSelector: 'HOTEL',
          mrhstSelectorUseJson: true,
        },
      },
      {
        path: 'room-booking-application-form',
        loadComponent: () =>
          import(
            './components/documents/room-booking-application-form-document/room-booking-application-form-document.component'
          ).then((m) => m.RoomBookingApplicationFormDocumentComponent),
        resolve: {
          brand: brandDetailAccountResolver,
          list: bookingDocumentListResolver,
        },
        data: {
          type: 'room-booking-application-form',
        },
      },
      {
        path: 'room-change-application-form',
        loadComponent: () =>
          import(
            './components/documents/room-change-application-form-document/room-change-application-form-document.component'
          ).then((m) => m.RoomChangeApplicationFormDocumentComponent),
        resolve: {
          brand: brandDetailAccountResolver,
          list: bookingDocumentListResolver,
        },
        data: {
          type: 'room-change-application-form',
        },
      },
      {
        path: 'room-cancel-application-form',
        loadComponent: () =>
          import(
            './components/documents/room-cancel-application-form-document/room-cancel-application-form-document.component'
          ).then((m) => m.RoomCancelApplicationFormDocumentComponent),
        resolve: {
          brand: brandDetailAccountResolver,
          list: bookingDocumentListResolver,
        },
        data: {
          type: 'room-cancel-application-form',
        },
      },
      {
        path: 'room-final-confirmation-form',
        loadComponent: () =>
          import(
            './components/documents/room-final-confirmation-form-document/room-final-confirmation-form-document.component'
          ).then((m) => m.RoomFinalConfirmationFormDocumentComponent),
        resolve: {
          brand: brandDetailAccountResolver,
          list: bookingDocumentListResolver,
        },
        data: {
          type: 'room-final-confirmation-form',
        },
      },
      {
        path: 'room-booking-application-form/:id',
        loadComponent: () =>
          import(
            './components/documents/room-booking-application-form-document/room-booking-application-form-document.component'
          ).then((m) => m.RoomBookingApplicationFormDocumentComponent),
        resolve: {
          data: bookingDetailResolver,
          brand: brandDetailAccountResolver,
        },
        data: {
          type: 'room-booking-application-form',
          mrhstSelector: 'HOTEL',
        },
      },
      {
        path: 'room-change-application-form/:id',
        loadComponent: () =>
          import(
            './components/documents/room-change-application-form-document/room-change-application-form-document.component'
          ).then((m) => m.RoomChangeApplicationFormDocumentComponent),
        resolve: {
          data: bookingDetailResolver,
          brand: brandDetailAccountResolver,
        },
        data: {
          type: 'room-change-application-form',
          mrhstSelector: 'HOTEL',
        },
      },
      {
        path: 'room-cancel-application-form/:id',
        loadComponent: () =>
          import(
            './components/documents/room-cancel-application-form-document/room-cancel-application-form-document.component'
          ).then((m) => m.RoomCancelApplicationFormDocumentComponent),
        resolve: {
          data: bookingDetailResolver,
          brand: brandDetailAccountResolver,
        },
        data: {
          type: 'room-cancel-application-form',
          mrhstSelector: 'HOTEL',
        },
      },
      {
        path: 'room-final-confirmation-form/:id',
        loadComponent: () =>
          import(
            './components/documents/room-final-confirmation-form-document/room-final-confirmation-form-document.component'
          ).then((m) => m.RoomFinalConfirmationFormDocumentComponent),
        resolve: {
          data: bookingDetailResolver,
          brand: brandDetailAccountResolver,
        },
        data: {
          type: 'room-final-confirmation-form',
          mrhstSelector: 'HOTEL',
        },
      },
    ],
  },
];
